/**
 * Compatibility helpers for cached lazysizes noscript markup and print.
 *
 * New image layouts emit a real `<picture>` with native `loading="lazy"`.
 * Cached HTML may still wrap pictures in `[data-noscript]` until caches expire.
 */
class NoscriptImageHelper {
    /**
     * Move `<picture>` / `<img>` out of leftover `<noscript>` placeholders.
     *
     * @param {ParentNode} [root=document] Scope to search within
     */
    static unwrapAll(root = document) {
        for (const el of root.querySelectorAll('[data-noscript]')) {
            NoscriptImageHelper.unwrapPlaceholder(el);
        }
    }

    /**
     * Expand one lazysizes-style placeholder if it has no visible picture yet.
     *
     * @param {Element} el Wrapper with `data-noscript`
     */
    static unwrapPlaceholder(el) {
        if (!(el instanceof HTMLElement)) {
            return;
        }

        if (!el.querySelector('picture, img')) {
            const noScript = el.querySelector('noscript, script[type*="html"]');
            const content = noScript
                ? (noScript.textContent || noScript.innerText)
                : '';
            if (content) {
                el.innerHTML = content;
            }
        }

        const media = el.querySelector('picture, img');
        if (media && el.parentNode) {
            el.replaceWith(media);
        }
    }

    /**
     * Force lazy images to load before printing (Chrome otherwise often omits them).
     */
    static enableLazyImagesForPrint() {
        window.addEventListener('beforeprint', () => {
            for (const img of document.querySelectorAll('img[loading="lazy"]')) {
                img.loading = 'eager';
            }
        });
    }
}

export default NoscriptImageHelper;
